import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useListConversationsQuery } from '~/api/endpoints/inbox'
import { useAppDispatch } from '~/app/hooks'
import { usePendingProposals } from '~/features/approvals/pending'
import { loggedOut } from '~/app/authSlice'
import { cx } from '~/lib/cx'
import { Button } from '~/components/primitives/Button'
import { ClientSwitcher } from '~/features/clients/ClientSwitcher'
import { useSelectedClientId } from '~/features/clients/useClients'
import { ThemeToggle } from './ThemeToggle'
import { ClassifierCreditBanner } from './ClassifierCreditBanner'

// In the order work happens: find people, put them in a campaign, answer the ones who
// write back, and keep the seats alive underneath all of it.
const NAV = [
  { to: '/', label: 'Overview', end: true },
  { to: '/prospecting', label: 'Prospecting', end: false },
  { to: '/campaigns', label: 'Campaigns', end: false },
  { to: '/inbox', label: 'Inbox', end: false },
  { to: '/approvals', label: 'Approvals', end: false },
  { to: '/accounts', label: 'Accounts', end: false },
  { to: '/clients', label: 'Clients', end: false },
] as const

export function AppShell({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch()
  // The one number worth carrying in the chrome: somebody is waiting for an answer.
  const clientId = useSelectedClientId()
  const { data: inbox } = useListConversationsQuery(
    { limit: 200, clientId },
    { pollingInterval: 60_000 },
  )
  const waiting = inbox?.items.length ?? 0
  // And the other: the agent has prepared something only a person may let run.
  const { data: proposals } = usePendingProposals()
  const awaitingApproval = proposals?.pending_count ?? 0

  return (
    <div className="flex h-dvh overflow-hidden bg-ground">
      <nav className="flex w-40 shrink-0 flex-col border-r border-line bg-surface">
        <div className="flex h-11 items-center gap-2 border-b border-line px-3">
          <span className="h-2 w-2 rounded-full bg-signal" aria-hidden />
          <span className="label text-text">Outreach</span>
        </div>
        <div className="border-b border-line p-1.5">
          <ClientSwitcher />
        </div>
        <ul className="flex flex-1 flex-col gap-px p-1.5">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cx(
                    'label flex h-7 items-center justify-between rounded-sm px-2 transition-colors',
                    isActive
                      ? 'bg-raised text-text'
                      : 'text-dim hover:bg-raised/60 hover:text-text',
                  )
                }
              >
                {item.label}
                {item.label === 'Inbox' && waiting > 0 ? (
                  <span className="tabular rounded-xs bg-live/15 px-1 text-2xs text-live">
                    {waiting}
                  </span>
                ) : null}
                {item.label === 'Approvals' && awaitingApproval > 0 ? (
                  <span
                    className="tabular rounded-xs bg-signal/15 px-1 text-2xs text-signal"
                    title={`${awaitingApproval} awaiting approval`}
                  >
                    {awaitingApproval}
                  </span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-1 border-t border-line p-1.5">
          <ThemeToggle />
          <Button size="sm" variant="ghost" onClick={() => dispatch(loggedOut())}>
            Sign out
          </Button>
        </div>
      </nav>
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <ClassifierCreditBanner />
        {children}
      </main>
    </div>
  )
}

export interface PageHeaderProps {
  title: ReactNode
  /** One line saying what this screen is for, when the title cannot carry it alone. */
  subtitle?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
}

export function PageHeader({ title, subtitle, actions, breadcrumb }: PageHeaderProps) {
  return (
    <header className="flex h-11 shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-4">
      <div className="flex min-w-0 items-baseline gap-2">
        {breadcrumb ? <span className="label shrink-0">{breadcrumb}</span> : null}
        <h1 className="truncate text-base font-medium">{title}</h1>
        {subtitle ? <span className="truncate text-xs text-dim">{subtitle}</span> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  )
}

/** The scrolling body of a page. Every screen puts its content here. */
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  const { key } = useLocation()
  return (
    <div key={key} className={cx('flex-1 overflow-y-auto p-4', className)}>
      {children}
    </div>
  )
}
